import DateFnsUtils from "@date-io/date-fns";
import { CssBaseline } from "@material-ui/core";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { SnackbarProvider } from "notistack";
import * as React from "react";

import {
    ErrorBoundary,
    RelodeRouter,
    RelodeTheme,
    RelodeThemeProvider,
    RouteTracker,
} from "../";
import {
    getKeyFromStorage,
    getUserFromStorage,
    setKeyInStorage,
    setStorageKey,
    telemetryService,
} from "../../modules";
import { NotificationsProvider } from "../notifications";
import { OfflineDialog } from "./offlineDialog";
import { useRelodeAppActions } from "./relodeAppActions";
import { RelodeAppConfig } from "./relodeAppConfig";
import { relodeAppReducer } from "./relodeAppReducer";
import { RelodeAppState } from "./relodeAppState";
import { RelodeAppUI } from "./relodeAppUI";

type IRelodeAppContext = {
    state: RelodeAppState;
    actions: ReturnType<typeof useRelodeAppActions>;
};

export const RelodeAppContext =
    React.createContext<IRelodeAppContext>(undefined);

export interface RelodeAppProps {
    children?: React.ReactNode;
    config: RelodeAppConfig;
    theme: RelodeTheme;
}

export const RelodeApp = React.memo((props: RelodeAppProps) => {
    setStorageKey(props.config.version);

    const settingsString = getKeyFromStorage("relode:settings");
    let settings: { user: { isMenuOpen: boolean } };
    if (settingsString) {
        settings = JSON.parse(settingsString);
    } else {
        settings = {
            user: {
                isMenuOpen: true,
            },
        };
        setKeyInStorage("relode:settings", JSON.stringify(settings));
    }

    const [state, dispatch] = React.useReducer(relodeAppReducer, {
        apiFailureCount: 0,
        config: props.config,
        currentUser: getUserFromStorage(),
        haltApiCalls: false,
        settings,
    });

    telemetryService.init();
    telemetryService.applicationStarted(props.config.appName);
    const actions = useRelodeAppActions(dispatch);

    React.useEffect(() => {
        if (state.haltApiCalls) {
            telemetryService.trackEvent("api calls halted");
        }
    }, [state.haltApiCalls]);

    const [isOnline, setIsOnline] = React.useState(navigator.onLine);

    React.useEffect(() => {
        const handleOffline = () => setIsOnline(false);
        const handleOnline = () => setIsOnline(true);

        window.addEventListener("offline", handleOffline);
        window.addEventListener("online", handleOnline);
        return () => {
            window.removeEventListener("offline", handleOffline);
            window.removeEventListener("online", handleOnline);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (isOnline) {
            actions.appOnline();
        } else {
            actions.appOffline();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOnline]);

    const value = React.useMemo(
        () => ({
            state,
            actions,
        }),
        [state, actions]
    );

    return (
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <RelodeAppContext.Provider value={value}>
                <RelodeAppUI>
                    <RelodeThemeProvider theme={props.theme}>
                        <CssBaseline />
                        <RelodeRouter baseUrl={props.config.appName}>
                            <RouteTracker />
                            <ErrorBoundary config={props.config}>
                                <SnackbarProvider maxSnack={3}>
                                    <NotificationsProvider>
                                        {props.children}
                                    </NotificationsProvider>
                                </SnackbarProvider>
                                <OfflineDialog isOpen={state.haltApiCalls} />
                            </ErrorBoundary>
                        </RelodeRouter>
                    </RelodeThemeProvider>
                </RelodeAppUI>
            </RelodeAppContext.Provider>
        </MuiPickersUtilsProvider>
    );
});

export const useRelodeApp = () => {
    const context = React.useContext(RelodeAppContext);
    if (context === undefined) {
        throw new Error("useRelodeApp must be used with a RelodeApp component");
    }

    return context;
};
